import { useEffect, useRef, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { formatISO } from "date-fns";
import { FinancialAccountType } from "@common/types/financialAccount";
import { createCashItemBodySchema } from "@common/schemas/domains/cashItem/cashItem.schema";
import { BankCashItemType } from "@common/types/bankCashItem";
import { submitHelper } from "@helpers/form";
import { useCreateCheckMutation } from "@api/checks.api";
import { useCreateDepositMutation } from "@api/deposits.api";
import { useLastReconciliationForAccount } from "@api/reconciliation.api";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useOverlays } from "contexts/OverlayContext";
import { Button } from "@design-system/Button/Button";
import { useConstant } from "@hooks/useConstant";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { ImportCashItemsButton } from "@components/cashItemImporter/ImportCashItemsButton";
import { RecordCheckFormFields } from "./RecordCheckFormFields";
import { RecordCheckLines } from "./RecordCheckLines";
import {
    canSubmitRecordCheck,
    emptyCheckLine,
    hasEnteredCashItemDetails,
    RecordCheckFormValues,
    toCreateCheckBody,
    validateRecordCheckForm,
} from "./recordCheckForm";
import { useAddedCashItemToast } from "./useAddedCashItemToast";
import styles from "./RecordCheckModal.module.scss";

interface Props {
    type: BankCashItemType;
    onHide: () => void;
    initialFinancialAccountId?: number;
}

export const RecordCheckModal = ({
    type,
    onHide,
    initialFinancialAccountId,
}: Props) => {
    const showAddedToast = useAddedCashItemToast();
    const { openOverlay } = useOverlays();
    const [loading, setLoading] = useState(false);
    const closeAfterSave = useRef(true);
    const { enabled: isGlFirst } = useIsGlFirst();
    const startsAsCheck = type === BankCashItemType.CHECK;
    const createCheck = useCreateCheckMutation();
    const createDeposit = useCreateDepositMutation();
    const financialAccounts = useFinancialAccounts();
    const today = useConstant(() =>
        formatISO(new Date(), { representation: "date" }),
    );
    const lastReconciledRef = useRef<{
        financialAccountId?: number;
        closingDate?: string;
    }>({});

    const initialAccountId = financialAccounts.some(
        (account) =>
            account.id === initialFinancialAccountId &&
            (!startsAsCheck ||
                account.financialType === FinancialAccountType.CHECKING),
    )
        ? initialFinancialAccountId
        : undefined;

    const formik = useFormik<RecordCheckFormValues>({
        validateOnBlur: true,
        validateOnChange: true,
        initialTouched: { date: true },
        initialValues: {
            type,
            financialAccountId: initialAccountId,
            date: today,
            amount: undefined,
            checkNumber: "",
            counterpartyId: "",
            memo: "",
            lines: [emptyCheckLine()],
        },
        validate: (values) =>
            validateRecordCheckForm({
                values,
                lastReconciledClosingDate:
                    values.financialAccountId ===
                    lastReconciledRef.current.financialAccountId
                        ? lastReconciledRef.current.closingDate
                        : undefined,
            }),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async (values) => {
                const body = createCashItemBodySchema.parse(
                    toCreateCheckBody(values),
                );
                const isCheck = values.type === BankCashItemType.CHECK;
                const { transactionId } = isCheck
                    ? (await createCheck.mutateAsync(body)).check
                    : (await createDeposit.mutateAsync(body)).deposit;
                showAddedToast({
                    type: values.type,
                    transactionId,
                    entityId:
                        financialAccounts.find(
                            (account) =>
                                account.id === values.financialAccountId,
                        )?.entityId ?? undefined,
                });
                if (closeAfterSave.current) {
                    onHide();
                    return;
                }
                openOverlay(isCheck ? "recordCheck" : "recordDeposit", {
                    financialAccountId: values.financialAccountId,
                });
            },
        }),
    });

    const isCheck = formik.values.type === BankCashItemType.CHECK;
    const entityId =
        financialAccounts.find(
            (account) => account.id === formik.values.financialAccountId,
        )?.entityId ?? undefined;
    const { lastReconciliation } = useLastReconciliationForAccount(
        formik.values.financialAccountId,
        entityId,
    );
    const lastReconciledClosingDate = lastReconciliation?.closingDate;
    lastReconciledRef.current = {
        financialAccountId: formik.values.financialAccountId,
        closingDate: lastReconciledClosingDate,
    };
    const validateFormRef = useRef(formik.validateForm);
    validateFormRef.current = formik.validateForm;
    useEffect(() => {
        void validateFormRef.current();
    }, [lastReconciledClosingDate]);
    const canSubmit =
        !loading &&
        canSubmitRecordCheck({
            values: formik.values,
            isGlFirst,
            lastReconciledClosingDate,
        });
    const unsavedItemKind = isCheck ? "check" : "deposit";
    const hasUnsavedCashItemDetails = hasEnteredCashItemDetails({
        values: formik.values,
        initialDate: formik.initialValues.date,
    });

    return (
        <>
            <StandardModalHeader>Add transaction</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <Form onSubmit={formik.handleSubmit}>
                        <VStack gap={12}>
                            <RecordCheckFormFields />
                            {entityId === undefined && (
                                <span className={styles.linesHint}>
                                    Select an account above to choose ledger
                                    accounts for each line.
                                </span>
                            )}
                            <RecordCheckLines
                                entityId={entityId}
                                isGlFirst={isGlFirst}
                            />
                        </VStack>
                        <HStack
                            as="footer"
                            justify="end"
                            align="center"
                            gap={12}
                            className="mt-3"
                        >
                            <ImportCashItemsButton
                                financialAccountId={
                                    formik.values.financialAccountId
                                }
                                unsavedItemLabel={
                                    hasUnsavedCashItemDetails
                                        ? unsavedItemKind
                                        : undefined
                                }
                            />
                            <Button
                                variant="link"
                                size="sm"
                                type="button"
                                onClick={onHide}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="secondary"
                                size="md"
                                type="button"
                                disabled={!canSubmit}
                                onClick={() => {
                                    closeAfterSave.current = false;
                                    void formik.submitForm();
                                }}
                            >
                                Save and new
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                type="submit"
                                disabled={!canSubmit}
                                onClick={() => {
                                    closeAfterSave.current = true;
                                }}
                            >
                                {isCheck ? "Add check" : "Add deposit"}
                            </Button>
                        </HStack>
                    </Form>
                </FormikProvider>
            </StandardModalBody>
        </>
    );
};
